import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './constants';

const JsonPatientForm = () => {
    const [message, setMessage] = useState('');
    const [validationError, setError] = useState('');
    const [descFile, setDescFile] = useState(null);
    const [datFile, setDatFile] = useState(null);
    const [ppgFile, setPpgFile] = useState(null);
    const [tempFile, setTempFile] = useState(null);
    const [loading, setLoading] = useState(false);

    // Handle file input change for .dat file
    const handleDatFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setDatFile(selectedFile);
            setDescFile(null); // Reset descriptor file when data file changes
            setPpgFile(null);
            setTempFile(null);
            setError('');
        }
    };

    // Handle file input change for PPG .dat file
    const handlePpgFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            const baseFileName = datFile.name.split('.').slice(0, -1).join('.');
            const expectedFileName = `${baseFileName}_PPG.dat`;

            if (selectedFile.name === expectedFileName) {
                setPpgFile(selectedFile);
                setError('');
            } else {
                setError(`Please upload the corresponding ${expectedFileName} file`);
            }
        }
    };

    // Handle file input change for Temp .dat file
    const handleTempFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            const baseFileName = datFile.name.split('.').slice(0, -1).join('.');
            const expectedFileName = `${baseFileName}_Temp.dat`;

            if (selectedFile.name === expectedFileName) {
                setTempFile(selectedFile);
                setError('');
            } else {
                setError(`Please upload the corresponding ${expectedFileName} file`);
            }
        }
    };

    // Handle the description file upload (JSON only)
    const handleDescFileChange = (event) => {
        const file = event.target.files[0];
        const baseFileName = datFile.name.split('.').slice(0, -1).join('.');
        const expectedFileName = `${baseFileName}_desc.json`;

        if (file && file.name === expectedFileName) {
            setDescFile(file);
            setError('');
        } else {
            setError(`Please upload the corresponding ${expectedFileName} file`);
        }
    };

    // Function to clear the form data
    const clearForm = () => {
        setError('');
        setMessage('');
        setDescFile(null);
        setDatFile(null);
        setPpgFile(null);
        setTempFile(null);
        setLoading(false);
        // Clear file input fields
        document.getElementById('jsonDatFileInput').value = '';
        if (document.getElementById('jsonDescFileInput')) {
            document.getElementById('jsonDescFileInput').value = '';
        }
        if (document.getElementById('jsonPpgFileInput')) {
            document.getElementById('jsonPpgFileInput').value = '';
        }
        if (document.getElementById('jsonTempFileInput')) {
            document.getElementById('jsonTempFileInput').value = '';
        }
    };

    // Convert file to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Remove the data URL prefix (e.g., "data:application/octet-stream;base64,")
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!datFile || !descFile) {
            setMessage('Please select both .dat and _desc.json files before submitting.');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            // Read the JSON descriptor file
            const jsonContent = await descFile.text();
            const jsonMeta = JSON.parse(jsonContent);

            // Convert .dat file to base64
            const base64Buffer = await fileToBase64(datFile);

            // Convert PPG file to base64 if present
            let base64PpgBuffer = null;
            if (ppgFile) {
                base64PpgBuffer = await fileToBase64(ppgFile);
            }

            // Convert Temp file to base64 if present
            let base64TempBuffer = null;
            if (tempFile) {
                base64TempBuffer = await fileToBase64(tempFile);
            }

            // Send to server using the upload.js endpoint
            const response = await axios.post(`${API_BASE_URL}/upload`, {
                jsonMeta,
                base64Buffer,
                base64PpgBuffer,
                base64TempBuffer
            });

            setMessage(`Test data uploaded successfully! ${response.data.message}`);
        } catch (error) {
            console.error('Error:', error);
            if (error.response) {
                setMessage(`Error uploading test data: ${error.response.data.error || error.response.data.message}`);
            } else if (error.message) {
                setMessage(`Error: ${error.message}`);
            } else {
                setMessage('Error uploading test data. Please check the files and try again.');
            }
        }

        setLoading(false);
    };

    return (
        <div>
            <h2>Upload Test Data with JSON Descriptor</h2>
            <p>Upload test data files where the descriptor is in JSON format (_desc.json)</p>

            <div className="file-upload">
                <h3>Test Data File (.dat): </h3>
                <input
                    type="file"
                    id="jsonDatFileInput"
                    name="jsonDatFileInput"
                    onChange={handleDatFileChange}
                    accept=".dat"
                />
            </div>

            {datFile && (
                <div>
                    <h3>Upload JSON Descriptor File (_desc.json): </h3>
                    <input
                        type="file"
                        id="jsonDescFileInput"
                        name="jsonDescFileInput"
                        onChange={handleDescFileChange}
                        accept=".json"
                    />
                </div>
            )}

            {datFile && descFile && (
                <div>
                    <h3>Upload PPG Data File (_PPG.dat) - Optional: </h3>
                    <input
                        type="file"
                        id="jsonPpgFileInput"
                        name="jsonPpgFileInput"
                        onChange={handlePpgFileChange}
                        accept=".dat"
                    />
                    {ppgFile && <p style={{ color: 'green' }}>✓ PPG file loaded: {ppgFile.name}</p>}
                </div>
            )}

            {datFile && descFile && (
                <div>
                    <h3>Upload Temperature Data File (_Temp.dat) - Optional: </h3>
                    <input
                        type="file"
                        id="jsonTempFileInput"
                        name="jsonTempFileInput"
                        onChange={handleTempFileChange}
                        accept=".dat"
                    />
                    {tempFile && <p style={{ color: 'green' }}>✓ Temperature file loaded: {tempFile.name}</p>}
                </div>
            )}

            {validationError && <p style={{ color: 'red' }}>{validationError}</p>}

            <div style={{ marginTop: '20px' }}>
                {loading ? (
                    <div className="spinner"></div>
                ) : (
                    <button
                        onClick={handleSubmit}
                        disabled={!datFile || !descFile || loading}
                    >
                        Submit
                    </button>
                )}
                <button
                    type="button"
                    onClick={clearForm}
                    style={{ marginLeft: '10px' }}
                >
                    Clear
                </button>
            </div>

            {message && <p>{message}</p>}
        </div>
    );
};

export default JsonPatientForm;
